import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  assignUserToOrg,
  createOrg,
  createSponsorship,
  deleteSponsorship,
  findUser,
  getAllOrganizations,
  inviteUserToOrg,
  listOrgs,
  listSponsorships,
  updateOrg,
  updateSponsorship,
  updateUser,
} from "./firestoreService";

// SU-only operator tool — strings are intentionally English-only (only
// superusers reach this page). The shared Dashboard card uses i18n keys.

const GUIDELINES = ["EUCAST", "CLSI"];
const LANGUAGES = ["en", "ru", "tg", "es", "pt-BR", "vi"];
const PLANS = [
  { value: "team_pro", label: "Team PRO" },
  { value: "personal_pro", label: "Personal PRO" },
];
const FUNDING_TYPES = [
  { value: "complimentary", label: "Complimentary" },
  { value: "grant", label: "Grant" },
  { value: "consulting", label: "Consulting" },
  { value: "other", label: "Other" },
];
const ASSIGN_ROLES = [
  { value: "admin", label: "admin (org lead)" },
  { value: "user", label: "user (member)" },
  { value: "pro_user", label: "pro_user (individual)" },
];
const USER_STATUSES = [
  { value: "active", label: "active" },
  { value: "inactive", label: "inactive" },
];

// Section accent colors (echo the Dashboard card palette).
const COLORS = { sponsor: "#00897b", org: "#1976d2", user: "#388e3c" };

const EMPTY_SPONSOR_FORM = {
  name: "",
  shortName: "",
  fundingType: "complimentary",
  amount: "",
  currency: "USD",
  reference: "",
  startDate: "",
  endDate: "",
  notes: "",
};

const EMPTY_ORG_FORM = {
  name: "",
  shortName: "",
  primaryGuideline: "EUCAST",
  preferredLanguage: "en",
  plan: "team_pro",
  sponsorshipId: "",
  validUntil: "",
  seatLimit: "",
};

const EMPTY_ASSIGN_FORM = { email: "", orgId: "", role: "user" };
const EMPTY_INVITE_FORM = { email: "", orgId: "" };

const fmtDate = (millis) =>
  millis ? new Date(millis).toLocaleDateString() : "—";

const toDateInput = (millis) => {
  if (!millis) return "";
  const d = new Date(millis);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const orgLabel = (o) => {
  const q = (o.entitlement && o.entitlement.plan) || o.tier || null;
  return q ? `${o.name} (${q})` : o.name || o.id;
};

function Provisioning() {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState(null); // { section, severity, text }
  const [dialogError, setDialogError] = useState("");

  const [sponsorships, setSponsorships] = useState([]);
  const [orgs, setOrgs] = useState([]); // ALL orgs
  const [allOrgs, setAllOrgs] = useState([]); // full docs, for assign/invite/user dropdowns

  const [sponsorForm, setSponsorForm] = useState(EMPTY_SPONSOR_FORM);
  const [orgForm, setOrgForm] = useState(EMPTY_ORG_FORM);
  const [assignForm, setAssignForm] = useState(EMPTY_ASSIGN_FORM);
  const [inviteForm, setInviteForm] = useState(EMPTY_INVITE_FORM);

  const [editSponsor, setEditSponsor] = useState(null);
  const [deleteSponsorTarget, setDeleteSponsorTarget] = useState(null);
  const [editOrg, setEditOrg] = useState(null);

  const [userQuery, setUserQuery] = useState("");
  const [foundUser, setFoundUser] = useState(null);
  const [userForm, setUserForm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const [sp, og, all] = await Promise.all([
        listSponsorships(),
        listOrgs(),
        getAllOrganizations(),
      ]);
      setSponsorships(sp.rows || []);
      setOrgs(og.rows || []);
      setAllOrgs(
        [...(all || [])].sort((a, b) =>
          (a.name || "").localeCompare(b.name || ""),
        ),
      );
    } catch (e) {
      setLoadError(e.message || "Failed to load provisioning data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const fieldH = (setter) => (k) => (e) =>
    setter((f) => ({ ...f, [k]: e.target.value }));

  const setSponsor = fieldH(setSponsorForm);
  const setOrg = fieldH(setOrgForm);
  const setAssign = fieldH(setAssignForm);
  const setInvite = fieldH(setInviteForm);
  const setEditSponsorF = fieldH(setEditSponsor);
  const setEditOrgF = fieldH(setEditOrg);
  const setUserF = fieldH(setUserForm);

  const runAction = async (section, fn, onOk) => {
    setBusy(section);
    setMsg(null);
    try {
      const result = await fn();
      const text = onOk(result);
      setMsg({ section, severity: "success", text });
      await load();
    } catch (e) {
      setMsg({ section, severity: "error", text: e.message || "Action failed." });
    } finally {
      setBusy(null);
    }
  };

  const runDialog = async (busyKey, fn, onOk) => {
    setBusy(busyKey);
    setDialogError("");
    try {
      const result = await fn();
      onOk(result);
      await load();
    } catch (e) {
      setDialogError(e.message || "Action failed.");
    } finally {
      setBusy(null);
    }
  };

  const sectionAlert = (section) =>
    msg && msg.section === section ? (
      <Alert severity={msg.severity} sx={{ mt: 2 }} onClose={() => setMsg(null)}>
        {msg.text}
      </Alert>
    ) : null;

  const sectionHeader = (label, color, description) => (
    <Box
      sx={{
        mt: 4,
        mb: 2,
        px: 2,
        py: 1.5,
        borderLeft: `6px solid ${color}`,
        bgcolor: "action.hover",
        borderRadius: 1,
      }}
    >
      <Typography variant="h5" component="h2">
        {label}
      </Typography>
      {description && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {description}
        </Typography>
      )}
    </Box>
  );

  // ---------- payloads ----------
  const sponsorPayload = (f) => ({
    name: f.name,
    shortName: f.shortName,
    fundingType: f.fundingType,
    amount: f.amount,
    currency: f.currency,
    reference: f.reference,
    startDate: f.startDate || null,
    endDate: f.endDate || null,
    notes: f.notes,
  });

  const orgPayload = (f) => ({
    name: f.name,
    shortName: f.shortName,
    primaryGuideline: f.primaryGuideline,
    preferredLanguage: f.preferredLanguage,
    sponsorshipId: f.sponsorshipId || null,
    entitlement: {
      plan: f.plan,
      validUntil: f.validUntil || null,
      seatLimit: f.seatLimit,
    },
  });

  // ---------- create ----------
  const onCreateSponsorship = () =>
    runAction(
      "sponsor",
      () => createSponsorship(sponsorPayload(sponsorForm)),
      ({ sponsorshipId }) => {
        const name = sponsorForm.name;
        setSponsorForm(EMPTY_SPONSOR_FORM);
        return `Created sponsorship ${sponsorshipId} (${name}).`;
      },
    );

  const onCreateOrg = () =>
    runAction(
      "org",
      () => createOrg(orgPayload(orgForm)),
      ({ orgId }) => {
        const name = orgForm.name;
        setAssignForm((f) => ({ ...f, orgId }));
        setInviteForm((f) => ({ ...f, orgId }));
        setOrgForm((f) => ({ ...EMPTY_ORG_FORM, sponsorshipId: f.sponsorshipId }));
        return `Created org ${orgId} (${name}).`;
      },
    );

  const onAssign = () =>
    runAction(
      "assign",
      () =>
        assignUserToOrg({
          email: assignForm.email.trim(),
          orgId: assignForm.orgId,
          role: assignForm.role,
        }),
      (res) => {
        const email = res.email || assignForm.email;
        setAssignForm((f) => ({ ...EMPTY_ASSIGN_FORM, orgId: f.orgId }));
        return (
          `Assigned ${email} to ${res.orgId} as ${res.role}. ` +
          `They must sign out and back in to pick up the change.`
        );
      },
    );

  const onInvite = () =>
    runAction(
      "invite",
      () =>
        inviteUserToOrg({
          email: inviteForm.email.trim(),
          orgId: inviteForm.orgId,
        }),
      () => {
        const email = inviteForm.email;
        setInviteForm((f) => ({ ...EMPTY_INVITE_FORM, orgId: f.orgId }));
        return `Invite sent to ${email}. They join as a member; promote with Assign if needed.`;
      },
    );

  // ---------- edit / delete ----------
  const openEditSponsor = (s) =>
    setEditSponsor({
      sponsorshipId: s.sponsorshipId,
      name: s.name || "",
      shortName: s.shortName || "",
      fundingType: s.fundingType === "comp" ? "complimentary" : (s.fundingType || "complimentary"),
      amount: s.amount ?? "",
      currency: s.currency || "",
      reference: s.reference || "",
      startDate: toDateInput(s.startDate),
      endDate: toDateInput(s.endDate),
      status: s.status || "active",
      notes: s.notes || "",
    });

  const onSaveSponsor = () =>
    runDialog(
      "editSponsor",
      () =>
        updateSponsorship({
          sponsorshipId: editSponsor.sponsorshipId,
          ...sponsorPayload(editSponsor),
          status: editSponsor.status,
        }),
      () => {
        setMsg({ section: "sponsor", severity: "success", text: "Sponsorship updated." });
        setEditSponsor(null);
      },
    );

  const onConfirmDeleteSponsor = () =>
    runDialog(
      "deleteSponsor",
      () => deleteSponsorship(deleteSponsorTarget.sponsorshipId),
      () => {
        setMsg({ section: "sponsor", severity: "success", text: "Sponsorship deleted." });
        setDeleteSponsorTarget(null);
      },
    );

  const openEditOrg = (o) =>
    setEditOrg({
      orgId: o.orgId,
      name: o.name || "",
      shortName: o.shortName || "",
      primaryGuideline: o.primaryGuideline || "EUCAST",
      preferredLanguage: o.preferredLanguage || "en",
      // For a self-service org with no entitlement yet, seed the plan from its tier.
      plan: o.plan || (o.tier === "personal" ? "personal_pro" : "team_pro"),
      sponsorshipId: o.sponsorshipId || "",
      validUntil: toDateInput(o.validUntil),
      seatLimit: o.seatLimit ?? "",
    });

  const onSaveOrg = () =>
    runDialog(
      "editOrg",
      () => updateOrg({ orgId: editOrg.orgId, ...orgPayload(editOrg) }),
      () => {
        setMsg({ section: "org", severity: "success", text: "Organization updated." });
        setEditOrg(null);
      },
    );

  // ---------- user editor ----------
  const onFindUser = () =>
    runAction(
      "userFind",
      () => findUser(userQuery.trim()),
      (u) => {
        setFoundUser(u);
        setUserForm({
          uid: u.uid,
          email: u.email || "",
          displayName: u.displayName || "",
          status: u.status || "active",
          preferredLanguage: u.preferredLanguage || "en",
          role: u.role || "user",
          organizationId: u.organizationId || "",
        });
        return `Loaded ${u.email}.`;
      },
    );

  const onSaveUser = () =>
    runAction(
      "userSave",
      () =>
        updateUser({
          uid: userForm.uid,
          displayName: userForm.displayName,
          status: userForm.status,
          preferredLanguage: userForm.preferredLanguage,
        }),
      () => {
        // Collapse the editor on success (keeps the success alert, which lives
        // outside this block).
        setFoundUser(null);
        setUserForm(null);
        setUserQuery("");
        return "User profile updated.";
      },
    );

  // ---------- shared field groups ----------
  const sponsorFields = (form, onField) => (
    <Stack spacing={2}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <TextField label="Name" value={form.name} onChange={onField("name")} fullWidth required />
        <TextField label="Short name (≤25)" value={form.shortName} onChange={onField("shortName")} inputProps={{ maxLength: 25 }} fullWidth />
        <TextField select label="Funding type" value={form.fundingType} onChange={onField("fundingType")} fullWidth>
          {FUNDING_TYPES.map((ft) => (<MenuItem key={ft.value} value={ft.value}>{ft.label}</MenuItem>))}
        </TextField>
      </Stack>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <TextField label="Amount (optional)" type="number" value={form.amount} onChange={onField("amount")} inputProps={{ min: 0 }} fullWidth />
        <TextField label="Currency" value={form.currency} onChange={onField("currency")} inputProps={{ maxLength: 8 }} fullWidth />
        <TextField label="Reference (contract/project #)" value={form.reference} onChange={onField("reference")} fullWidth />
      </Stack>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <TextField label="Start date" type="date" value={form.startDate} onChange={onField("startDate")} InputLabelProps={{ shrink: true }} fullWidth />
        <TextField label="End date" type="date" value={form.endDate} onChange={onField("endDate")} InputLabelProps={{ shrink: true }} fullWidth />
        <TextField label="Notes" value={form.notes} onChange={onField("notes")} fullWidth />
      </Stack>
    </Stack>
  );

  const orgFields = (form, onField) => (
    <Stack spacing={2}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <TextField label="Organization name" value={form.name} onChange={onField("name")} fullWidth required />
        <TextField label="Short name (≤25)" value={form.shortName} onChange={onField("shortName")} inputProps={{ maxLength: 25 }} fullWidth />
      </Stack>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <TextField select label="Primary guideline" value={form.primaryGuideline} onChange={onField("primaryGuideline")} fullWidth>
          {GUIDELINES.map((g) => (<MenuItem key={g} value={g}>{g}</MenuItem>))}
        </TextField>
        <TextField select label="Preferred language" value={form.preferredLanguage} onChange={onField("preferredLanguage")} fullWidth>
          {LANGUAGES.map((l) => (<MenuItem key={l} value={l}>{l}</MenuItem>))}
        </TextField>
        <TextField select label="Plan" value={form.plan} onChange={onField("plan")} fullWidth>
          {PLANS.map((p) => (<MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>))}
        </TextField>
      </Stack>
      <Divider textAlign="left">
        <Typography variant="caption" color="text.secondary">Entitlement &amp; sponsorship</Typography>
      </Divider>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <TextField
          select
          label="Sponsorship"
          value={form.sponsorshipId}
          onChange={onField("sponsorshipId")}
          fullWidth
          helperText={sponsorships.length === 0 ? "Create a sponsorship first to group complimentary orgs" : "Which sponsorship funds this org (optional)"}
        >
          <MenuItem value="">(none)</MenuItem>
          {sponsorships.map((s) => (<MenuItem key={s.sponsorshipId} value={s.sponsorshipId}>{s.name}</MenuItem>))}
        </TextField>
        <TextField label="Valid until" type="date" value={form.validUntil} onChange={onField("validUntil")} InputLabelProps={{ shrink: true }} fullWidth helperText="Comp expiry — blank for none" />
        <TextField label="Seat limit" type="number" value={form.seatLimit} onChange={onField("seatLimit")} inputProps={{ min: 1 }} fullWidth helperText="Stored, not enforced yet" />
      </Stack>
    </Stack>
  );

  // ---------- grouping ----------
  const sponsorName = useMemo(() => {
    const m = new Map();
    for (const s of sponsorships) m.set(s.sponsorshipId, s.name);
    return m;
  }, [sponsorships]);

  const groupedOrgs = useMemo(() => {
    const map = new Map();
    for (const o of orgs) {
      const key = o.sponsorshipId || "__none__";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(o);
    }
    return [...map.entries()].sort((a, b) => {
      if (a[0] === "__none__") return 1;
      if (b[0] === "__none__") return -1;
      return (sponsorName.get(a[0]) || a[0]).localeCompare(sponsorName.get(b[0]) || b[0]);
    });
  }, [orgs, sponsorName]);

  return (
    <Container maxWidth="lg" sx={{ mt: 3, mb: 4 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h4" component="h1">Provisioning</Typography>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={loading || busy !== null}>
          Refresh
        </Button>
      </Stack>
      <Typography variant="body2" sx={{ mb: 1 }} color="text.secondary">
        Sponsorships are the accounting/reporting layer (who funds complimentary
        access). Organizations carry the plan; assign or invite users into them.
      </Typography>
      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setLoadError("")}>{loadError}</Alert>
      )}

      {/* ================= SPONSORSHIPS ================= */}
      {sectionHeader(
        "Sponsorships",
        COLORS.sponsor,
        "Accounting/reporting records of who funds complimentary access (a grant, a consulting arrangement, or your own comp licenses) and for what term. A sponsorship does NOT grant access and never touches payments — organizations link to one so you can track and report who paid for what, and until when.",
      )}

      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 0.5 }}>Create a sponsorship</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Create a funding record (e.g. “ADB – Tajikistan”, “KBQ Complimentary”).
          You attach organizations to it when you create or edit each org.
        </Typography>
        {sponsorFields(sponsorForm, setSponsor)}
        <Box sx={{ mt: 2 }}>
          <Button variant="contained" onClick={onCreateSponsorship} disabled={busy !== null || !sponsorForm.name.trim()}>
            {busy === "sponsor" ? <CircularProgress size={20} /> : "Create sponsorship"}
          </Button>
        </Box>
        {sectionAlert("sponsor")}
      </Paper>

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Existing sponsorships
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        “Orgs” = how many organizations are funded under each. Delete is blocked
        while any org still links to it — clear the link on each org first.
      </Typography>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress /></Box>
      ) : sponsorships.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: "center" }}>
          <Typography color="text.secondary">No sponsorships yet.</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Reference</TableCell>
                <TableCell>Term</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Orgs</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sponsorships.map((s) => (
                <TableRow key={s.sponsorshipId} hover>
                  <TableCell>{s.name || "—"}</TableCell>
                  <TableCell>{s.fundingType === "comp" ? "complimentary" : (s.fundingType || "—")}</TableCell>
                  <TableCell>{s.reference || "—"}</TableCell>
                  <TableCell>{fmtDate(s.startDate)} – {fmtDate(s.endDate)}</TableCell>
                  <TableCell>
                    <Chip size="small" label={s.status || "—"} color={s.status === "active" ? "success" : "default"} />
                  </TableCell>
                  <TableCell align="right">{s.orgCount}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button size="small" variant="outlined" disabled={busy !== null} onClick={() => openEditSponsor(s)}>Edit</Button>
                      <Button size="small" variant="outlined" color="error" disabled={busy !== null} onClick={() => { setDialogError(""); setDeleteSponsorTarget(s); }}>Delete</Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ================= ORGANIZATIONS ================= */}
      {sectionHeader(
        "Organizations",
        COLORS.org,
        "A lab / clinic / team. Its plan (Team PRO or Personal PRO) is what actually gates paid features. Optionally link it to a sponsorship and set a “valid until” date so complimentary access doesn’t linger.",
      )}

      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 0.5 }}>Create an organization</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Create a new organization with a plan and (optionally) a sponsorship +
          expiry. Use this for SU-provisioned complimentary / grant-funded labs.
        </Typography>
        {orgFields(orgForm, setOrg)}
        <Box sx={{ mt: 2 }}>
          <Button variant="contained" onClick={onCreateOrg} disabled={busy !== null || !orgForm.name.trim()}>
            {busy === "org" ? <CircularProgress size={20} /> : "Create organization"}
          </Button>
        </Box>
        {sectionAlert("org")}
      </Paper>

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        All organizations
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        SU-created, self-service signups, and the free Community org — grouped by
        sponsorship. Edit changes an org’s details, plan, expiry, and sponsorship
        link. The free / protected org can’t be edited here.
      </Typography>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>
      ) : orgs.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary">No organizations.</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Plan</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Valid until</TableCell>
                <TableCell>Seats</TableCell>
                <TableCell align="right">Members</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groupedOrgs.map(([key, rows]) => (
                <React.Fragment key={key}>
                  <TableRow>
                    <TableCell colSpan={7} sx={{ bgcolor: "action.hover", fontWeight: 600 }}>
                      {key === "__none__" ? "No sponsorship" : `Sponsorship: ${sponsorName.get(key) || key}`}
                    </TableCell>
                  </TableRow>
                  {rows.map((o) => (
                    <TableRow key={o.orgId} hover>
                      <TableCell>{o.name || "—"}</TableCell>
                      <TableCell>{o.plan ? <Chip size="small" label={o.plan} /> : "—"}</TableCell>
                      <TableCell>
                        {!o.hasEntitlement
                          ? "—"
                          : o.source === "paid"
                          ? `paid${o.provider ? ` (${o.provider})` : ""}`
                          : "complimentary"}
                      </TableCell>
                      <TableCell>{fmtDate(o.validUntil)}</TableCell>
                      <TableCell>{o.seatLimit ?? "∞"}</TableCell>
                      <TableCell align="right">{o.memberCount}</TableCell>
                      <TableCell align="right">
                        <Button size="small" variant="outlined" disabled={busy !== null} onClick={() => openEditOrg(o)}>Edit</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ================= USERS ================= */}
      {sectionHeader(
        "Users",
        COLORS.user,
        "Three tools, split by whether the person already has an account. Assign = move / upgrade / promote an EXISTING user. Invite = bring in a NEW person. Edit = fix an existing user’s profile fields.",
      )}

      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 0.5, fontWeight: 600 }}>Assign / move / upgrade / promote an existing user</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          For someone who ALREADY has an account. Sets which organization they
          belong to and their role — use it to upgrade a free user to a paid org,
          move a user between orgs, or promote a member to admin (assign them to
          their same org with role = admin). Their historical records stay with
          their previous org.
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
          <TextField label="User email" type="email" value={assignForm.email} onChange={setAssign("email")} fullWidth />
          <TextField select label="Organization" value={assignForm.orgId} onChange={setAssign("orgId")} fullWidth disabled={allOrgs.length === 0}>
            {allOrgs.map((o) => (<MenuItem key={o.id} value={o.id}>{orgLabel(o)}</MenuItem>))}
          </TextField>
          <TextField select label="Role" value={assignForm.role} onChange={setAssign("role")} fullWidth>
            {ASSIGN_ROLES.map((r) => (<MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>))}
          </TextField>
          <Button variant="contained" onClick={onAssign} disabled={busy !== null || !assignForm.email.trim() || !assignForm.orgId} sx={{ minWidth: 120, height: 56 }}>
            {busy === "assign" ? <CircularProgress size={20} /> : "Assign"}
          </Button>
        </Stack>
        {sectionAlert("assign")}

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle1" sx={{ mb: 0.5, fontWeight: 600 }}>Invite a NEW person to join an existing organization</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          For someone who does NOT yet have an account. Sends an email link; they
          self-enroll as a member. Refused if the email is already registered —
          use Assign for existing users.
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
          <TextField label="Invitee email" type="email" value={inviteForm.email} onChange={setInvite("email")} fullWidth />
          <TextField select label="Organization" value={inviteForm.orgId} onChange={setInvite("orgId")} fullWidth disabled={allOrgs.length === 0}>
            {allOrgs.map((o) => (<MenuItem key={o.id} value={o.id}>{orgLabel(o)}</MenuItem>))}
          </TextField>
          <Button variant="contained" onClick={onInvite} disabled={busy !== null || !inviteForm.email.trim() || !inviteForm.orgId} sx={{ minWidth: 120, height: 56 }}>
            {busy === "invite" ? <CircularProgress size={20} /> : "Invite"}
          </Button>
        </Stack>
        {sectionAlert("invite")}
      </Paper>

      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 0.5, fontWeight: 600 }}>Edit an existing user’s profile</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Change display name, status, and preferred language. To change a user’s
          organization or role, use Assign above. Superusers and protected users
          can’t be edited.
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
          <TextField label="Find user by email" type="email" value={userQuery} onChange={(e) => setUserQuery(e.target.value)} fullWidth />
          <Button variant="outlined" onClick={onFindUser} disabled={busy !== null || !userQuery.trim()} sx={{ minWidth: 120, height: 56 }}>
            {busy === "userFind" ? <CircularProgress size={20} /> : "Find"}
          </Button>
        </Stack>
        {sectionAlert("userFind")}
        {sectionAlert("userSave")}

        {foundUser && userForm && (
          <Box sx={{ mt: 2 }}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {userForm.email} — role <b>{userForm.role}</b>, org{" "}
              <b>{allOrgs.find((o) => o.id === userForm.organizationId)?.name || userForm.organizationId}</b>.
              {" "}Use Assign above to change role or organization. Email isn’t editable here.
            </Typography>
            <Stack spacing={2}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField label="Display name" value={userForm.displayName} onChange={setUserF("displayName")} fullWidth />
                <TextField select label="Status" value={userForm.status} onChange={setUserF("status")} fullWidth>
                  {USER_STATUSES.map((s) => (<MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>))}
                </TextField>
                <TextField select label="Preferred language" value={userForm.preferredLanguage} onChange={setUserF("preferredLanguage")} fullWidth>
                  {LANGUAGES.map((l) => (<MenuItem key={l} value={l}>{l}</MenuItem>))}
                </TextField>
              </Stack>
              <Box>
                <Button variant="contained" onClick={onSaveUser} disabled={busy !== null}>
                  {busy === "userSave" ? <CircularProgress size={20} /> : "Save user"}
                </Button>
              </Box>
            </Stack>
          </Box>
        )}
      </Paper>

      {/* ---------- dialogs ---------- */}
      <Dialog open={!!editSponsor} onClose={() => setEditSponsor(null)} maxWidth="md" fullWidth>
        <DialogTitle>Edit sponsorship</DialogTitle>
        <DialogContent>
          {dialogError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDialogError("")}>{dialogError}</Alert>}
          {editSponsor && (
            <Box sx={{ pt: 1 }}>
              {sponsorFields(editSponsor, setEditSponsorF)}
              <TextField select label="Status" value={editSponsor.status} onChange={setEditSponsorF("status")} sx={{ mt: 2, minWidth: 200 }}>
                <MenuItem value="active">active</MenuItem>
                <MenuItem value="closed">closed</MenuItem>
              </TextField>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditSponsor(null)} disabled={busy === "editSponsor"}>Cancel</Button>
          <Button variant="contained" onClick={onSaveSponsor} disabled={busy === "editSponsor" || !editSponsor?.name?.trim()}>
            {busy === "editSponsor" ? <CircularProgress size={20} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteSponsorTarget} onClose={() => setDeleteSponsorTarget(null)}>
        <DialogTitle>Delete sponsorship?</DialogTitle>
        <DialogContent>
          {dialogError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDialogError("")}>{dialogError}</Alert>}
          <DialogContentText>
            Delete sponsorship "{deleteSponsorTarget?.name}"? This is blocked if any
            organization still references it.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteSponsorTarget(null)} disabled={busy === "deleteSponsor"}>Cancel</Button>
          <Button color="error" variant="contained" onClick={onConfirmDeleteSponsor} disabled={busy === "deleteSponsor"}>
            {busy === "deleteSponsor" ? <CircularProgress size={20} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!editOrg} onClose={() => setEditOrg(null)} maxWidth="md" fullWidth>
        <DialogTitle>Edit organization</DialogTitle>
        <DialogContent>
          {dialogError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDialogError("")}>{dialogError}</Alert>}
          {editOrg && <Box sx={{ pt: 1 }}>{orgFields(editOrg, setEditOrgF)}</Box>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOrg(null)} disabled={busy === "editOrg"}>Cancel</Button>
          <Button variant="contained" onClick={onSaveOrg} disabled={busy === "editOrg" || !editOrg?.name?.trim()}>
            {busy === "editOrg" ? <CircularProgress size={20} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default Provisioning;
